import React from "react";
import Chart from "react-apexcharts";
import { Typography, Button } from "@material-ui/core";
import { IChartBase, pieChartColors, defaultChartWidth, defaultChartHeight } from "../general/charts";
import { v4 } from "uuid";
import ChartTitle from "../general/ChartTitle";

type IAggregationOptions = "count" | string[];

interface IHierarchicPieChartProps extends IChartBase {
    data: object[];
    levels: string[]; // levels for drilling down
    aggregationOptions: IAggregationOptions;
    showBreadcrumbs?: boolean;
    noLegend?: boolean;
}
interface IHierarchyLevel {
    name: string;
    value: string | number | null;
}

interface IHierarchicPieChartState {
    options: any;
    currentLevels: IHierarchyLevel[];
    siblings: ISibling[];
    series: number[];
    canZoomIn: boolean;
}

interface ISibling {
    name: string;
    value: number;
}

class HierarchicPieChart extends React.Component<IHierarchicPieChartProps, IHierarchicPieChartState> {
    findLevel(levels: IHierarchyLevel[], name: string): IHierarchyLevel {
        return levels.find(level => level.name === name);
    }

    canZoomOut() {
        return this.state.currentLevels.length > 1;
    }

    getSibling(objects: object[], aggregationOptions: IAggregationOptions, curentLevelName, e) {
        if (aggregationOptions === "count") {
            const value = objects.reduce((total, currentObject) => {
                return total + +(currentObject[curentLevelName] === e);
            }, 0);
            return {
                name: e,
                value: value || 0
            };
        } else if (Array.isArray(aggregationOptions)) {
            const value = objects.reduce((total, currentObject) => {
                const currentSum = aggregationOptions.reduce(
                    (total: number, valueToAdd) => total + currentObject[valueToAdd],
                    0
                );
                return currentObject[curentLevelName] === e ? total + currentSum : total;
            }, 0);
            return {
                name: e,
                value: value || 0
            };
        }
    }

    collectSiblings(
        data: object[],
        currentLevels: IHierarchyLevel[],
        aggregationOptions: IAggregationOptions
    ): ISibling[] {
        let relevantObjects = data;
        const curentLevelName = currentLevels[currentLevels.length - 1].name;
        currentLevels.forEach(level => {
            const currentLevel = this.findLevel(currentLevels, level.name);
            if (currentLevel.value) {
                relevantObjects = relevantObjects.filter(dataItem => dataItem[level.name] === level.value);
            }
        });
        const values = relevantObjects?.map(object => object[curentLevelName]);
        const uniq = [...new Set(values)];
        const ret = uniq.map(u => {
            return this.getSibling(relevantObjects, aggregationOptions, curentLevelName, u);
        });
        return ret;
    }

    zoomIn(name: string, value: number | string) {
        if (this.props.levels.length === 1) return;

        const lastLevel = {
            name,
            value
        };
        const newLevel = {
            name: this.props.levels[this.state.currentLevels.length],
            value: null
        };
        const currentLevels = [...this.state.currentLevels];
        currentLevels.splice(-1, 1, lastLevel, newLevel);
        const newSiblings = this.collectSiblings(this.props.data, currentLevels, this.props.aggregationOptions);

        this.setState({
            currentLevels: currentLevels,
            siblings: newSiblings,
            series: newSiblings.map(sibling => sibling.value),
            canZoomIn: currentLevels.length !== this.props.levels.length,
            options: {
                ...this.state.options,
                labels: newSiblings.map(sibling => sibling.name)
            }
        });
    }

    zoomOut() {
        if (!this.canZoomOut()) return;

        const lastLevel = {
            name: this.props.levels[this.state.currentLevels.length - 2],
            value: null
        };
        const currentLevels = [...this.state.currentLevels];
        currentLevels.splice(-2, 2, lastLevel);
        const newSiblings = this.collectSiblings(this.props.data, currentLevels, this.props.aggregationOptions);
        this.setState({
            currentLevels: currentLevels,
            canZoomIn: true,
            siblings: newSiblings,
            series: newSiblings.map(sibling => sibling.value),
            options: {
                ...this.state.options,
                labels: newSiblings.map(sibling => sibling.name)
            }
        });
    }

    getLevelsBreadCrumb(levels: IHierarchyLevel[]): string {
        return levels
            .filter(level => level.value)
            .map(level => level.value)
            .join(" → ");
    }

    constructor(props) {
        super(props);
        const siblings = this.collectSiblings(
            this.props.data,
            [{ name: this.props.levels[0], value: null }],
            this.props.aggregationOptions
        );

        this.state = {
            currentLevels: [{ name: this.props.levels[0], value: null }],
            siblings,
            series: siblings.map(sibling => sibling.value),
            canZoomIn: this.props.levels.length !== 1,
            options: {
                id: `hierarchic_pie_chart_${v4()}`,
                labels: siblings.map(sibling => sibling.name),
                legend: {
                    show: !this.props.noLegend,
                    position: "bottom",
                    horizontalAlign: "left",
                    floating: false
                },
                colors: pieChartColors,
                dataLabels: {
                    enabled: true,
                    formatter: function (value, { seriesIndex, dataPointIndex, w }) {
                        return w.config.labels[seriesIndex] + ":  " + w.config.series[seriesIndex];
                    },
                    dropShadow: {
                        enabled: false
                    },
                    style: {
                        fontSize: "12px",
                        fontWeight: "bold",
                        colors: ["#333333"]
                    }
                },
                chart: {
                    animation: {
                        enabled: true
                    },
                    type: "pie",
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            if (!this.state.canZoomIn) return;
                            this.props.onClick &&
                                this.props.onClick(
                                    config.dataPointIndex,
                                    config.w.config.labels[config.dataPointIndex]
                                );
                            this.zoomIn(
                                this.state.currentLevels[this.state.currentLevels.length - 1].name,
                                config.w.config.labels[config.dataPointIndex]
                            );
                        },
                        dataPointMouseEnter: event => {
                            if (event.path?.[0]) {
                                event.path[0].style.cursor =
                                    this.state.currentLevels.length === this.props.levels.length
                                        ? "default"
                                        : "pointer";
                            }
                        }
                    }
                },
                noData: {
                    text: "No data available",
                    style: {
                        fontSize: "24px"
                    }
                },
                plotOptions: {
                    pie: {
                        dataLabels: {
                            offset: 70,
                            minAngleToShowLabel: 10
                        },
                        donut: {
                            size: "0%",
                            background: "transparent"
                        }
                    }
                },
                stroke: {
                    width: 1,
                    colors: ["#ffffff"]
                },
                tooltip: {
                    fillSeriesColor: false,
                    theme: "light",
                    onDatasetHover: {
                        highlightDataSeries: true
                    }
                }
            }
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.data !== this.props.data) {
            const newSiblings = this.collectSiblings(
                this.props.data,
                [{ name: this.props.levels[0], value: null }],
                this.props.aggregationOptions
            );
            this.setState({
                currentLevels: [{ name: this.props.levels[0], value: null }],
                canZoomIn: true,
                siblings: newSiblings,
                series: newSiblings.map(sibling => sibling.value),
                options: {
                    ...this.state.options,
                    labels: newSiblings.map(sibling => sibling.name)
                }
            });
        }
    }
    render() {
        const width = this.props.width || defaultChartWidth;
        const height = this.props.height || defaultChartHeight;
        return (
            <div style={{ textAlign: "center", width, height, paddingBottom: "50px" }}>
                <div style={{ textAlign: "center", width: "100%" }}>
                    <ChartTitle title={this.props.title} secondaryTitle={this.props.secondaryTitle} />
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <span>
                            {this.props.showBreadcrumbs && (
                                <Typography variant="body2" component="span">
                                    {this.getLevelsBreadCrumb(this.state.currentLevels)}
                                </Typography>
                            )}
                        </span>
                        <span style={{ position: "absolute", right: 0 }}>
                            <Button
                                size="small"
                                onClick={() => this.zoomOut()}
                                variant="outlined"
                                disabled={!this.canZoomOut()}
                            >
                                Back
                            </Button>
                        </span>
                    </div>
                </div>
                <Chart
                    options={this.state.options}
                    series={this.state.series}
                    type="donut"
                    width="100%"
                    height="100%"
                />
            </div>
        );
    }
}

export default HierarchicPieChart;
